// Pure helpers for resolving a hardware slot (panel / inverter) when hydrating the
// DesignTool from a saved canvas. Extracted so the precedence is unit-testable.
//
// A slot can be persisted via two mutually-exclusive channels:
//   * an inline raw spec object (no registered hardware row) -> `spec`
//   * a registered-hardware ForeignKey object / id          -> `fkObject` / `idValue`
//
// Precedence on reload: raw spec -> FK object -> id lookup in the catalog -> none.
// The spec wins so an inline spec (whose id was inherited from the default during the
// settings merge) survives reload instead of collapsing back to the default hardware.

const INLINE_IDENTITY_KEYS = [
  'id',
  'pk',
  'hardware_id',
  'hardwareId',
  'companyPanelID',
  'companyPanelId',
  'companyInverterID',
  'companyInverterId',
];

const isObjectRecord = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * Inline specs do not identify a registered company-hardware row. Remove catalog
 * identity inherited from package defaults so callbacks cannot claim the default
 * hardware id while reporting a custom model.
 */
export function sanitizeInlineHardwareSpec(spec) {
  if (!isObjectRecord(spec)) return null;

  const sanitized = { ...spec };
  INLINE_IDENTITY_KEYS.forEach((key) => delete sanitized[key]);
  return Object.keys(sanitized).length ? sanitized : null;
}

/**
 * Snapshot the effective inline hardware values while keeping catalog identity out.
 * This preserves the same electrical fallbacks after reload without binding the spec
 * to the default package hardware row.
 */
export function buildEffectiveInlineHardwareSpec(defaultHardware, directSpec) {
  const sanitizedSpec = sanitizeInlineHardwareSpec(directSpec);
  if (!sanitizedSpec) return null;

  return sanitizeInlineHardwareSpec({
    ...(isObjectRecord(defaultHardware) ? defaultHardware : {}),
    ...sanitizedSpec,
  });
}

/**
 * Resolve one hardware slot from saved canvas data.
 *
 * @param {object}   args
 * @param {object=}  args.spec      canvas.selected_*_spec (inline spec object) or null
 * @param {object=}  args.fkObject  canvas.selected_* (serialized registered-hardware FK) or null
 * @param {*=}       args.idValue   canvas.selected_*_id (registered-hardware id) or null
 * @param {Array=}   args.catalog   available hardware to resolve idValue against
 * @returns {{ hardware: (object|null), source: ('spec'|'object'|'id'|'none') }}
 */
export function resolveSlotHardwareFromCanvas({
  spec = null,
  fkObject = null,
  idValue = null,
  catalog = [],
} = {}) {
  if (spec) {
    const sanitizedSpec = sanitizeInlineHardwareSpec(spec);
    if (sanitizedSpec) {
      return { hardware: sanitizedSpec, source: 'spec' };
    }
  }
  if (fkObject) {
    return { hardware: fkObject, source: 'object' };
  }
  if (idValue !== undefined && idValue !== null) {
    const match = (catalog || []).find(
      (h) => String(h?.id) === String(idValue)
    );
    if (match) {
      return { hardware: match, source: 'id' };
    }
  }
  return { hardware: null, source: 'none' };
}

/**
 * Match solar-production usage for registered hardware by id, or for an inline
 * spec by model when no catalog id exists.
 */
export function findPrimaryInverterUsage(invertersUsed, inverter) {
  if (!Array.isArray(invertersUsed) || !isObjectRecord(inverter)) return null;

  if (inverter.id !== undefined && inverter.id !== null && inverter.id !== '') {
    return (
      invertersUsed.find(
        (item) => String(item?.hardware_id) === String(inverter.id)
      ) || null
    );
  }

  if (!inverter.model) return null;
  return (
    invertersUsed.find(
      (item) => String(item?.model) === String(inverter.model)
    ) || null
  );
}
